import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Slide, SxProps, Theme } from '@mui/material';

interface StickyHeaderProps {
  children: React.ReactNode;
  stickyContent?: React.ReactNode;
  sx?: SxProps<Theme>;
  stickySx?: SxProps<Theme>;
  elevation?: number;
  component?: React.ElementType;
}

const StickyHeaderV2: React.FC<StickyHeaderProps> = ({
  children,
  stickyContent,
  sx,
  stickySx,
  elevation = 4,
  component = 'div',
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the original header is not visible, show sticky version
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      },
    );

    const currentRef = headerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <>
      {/* Original header that gets observed */}
      <Box ref={headerRef} component={component} sx={[{ width: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}>
        {children}
      </Box>

      {/* Sticky header that appears when original is not visible */}
      <Slide direction="down" in={isSticky}>
        <Paper
          elevation={elevation}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            // Fix for blurred content during/after transition
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitFontSmoothing: 'antialiased',
            ...stickySx,
          }}
        >
          {stickyContent || children}
        </Paper>
      </Slide>
    </>
  );
};

export default StickyHeaderV2;
